import * as React from "react";
import {
  Autocomplete,
  TextField,
  ListItemText,
  ClickAwayListener,
  Button,
  Paper,
  ListItem,
  Typography,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import classNames from "classnames";
import { useSearchParams } from "react-router-dom";
import { ISingleOption } from "./helper";
import {
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconPlus,
  IconProps,
  IconSearch,
} from "../../../../utils/icons";

interface Props {
  title: string;
  filterKey: string;
  thumbnails?: boolean;
  boxStyle?: any;
  icon?: (e: IconProps) => JSX.Element;
  options: Array<string>;
  optionsObjArray?: Array<ISingleOption>;
  updateSearch: (key: string, id?: string) => void;
  onCreateNew?: () => void;
  queryFilters?: any;
  onSearchChange?: (text: string) => void;
}

export function SingleFilter({
  title,
  icon,
  filterKey,
  thumbnails,
  optionsObjArray,
  updateSearch,
  onCreateNew,
  queryFilters,
  onSearchChange,
}: Props) {
  const [searchParams] = useSearchParams();
  const [showOptions, setShowOptions] = React.useState(false);
  const [value, setValue] = React.useState<ISingleOption>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  React.useEffect(() => {
    const val =
      (queryFilters ? queryFilters[filterKey] : searchParams.get(filterKey)) ||
      "";
    if (!optionsObjArray) setValue({ id: val, name: val });
    else {
      const elem = optionsObjArray.find((e) => e.id.toString() === val);
      setValue(elem);
    }
  }, [searchParams, optionsObjArray, queryFilters]);

  const onFilterValueChange = (_: any, data: any, reason: string) => {
    if (reason === "clear") updateSearch(filterKey);
    else updateSearch(filterKey, data.id);
  };

  const [brokenImages, setBrokenImages] = React.useState<Record<string, true>>({});

  const optionVisual = (option: ISingleOption) => {
    if (option.image && !brokenImages[option.id]) {
      return (
        <img
          src={option.image}
          alt={option.name}
          className="w-5 h-5 rounded-md object-cover bg-surface-2"
          onError={() => setBrokenImages((prev) => ({ ...prev, [option.id]: true }))}
        />
      );
    }
    if (option.icon) return option.icon;
    if (thumbnails) {
      return (
        <span className="w-5 h-5 rounded-md bg-surface-2 border border-border-token inline-flex items-center justify-center text-muted">
          <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 16.5 9 11l4 4 2.5-2.5L20 17" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="9" cy="8" r="1.4" />
            <rect x="3" y="4" width="18" height="16" rx="2.5" />
          </svg>
        </span>
      );
    }
    return icon?.({ width: 16, height: 16, stroke: "#B0B0B0" }) ?? null;
  };

  const renderOptionVisual = (option: ISingleOption) => {
    const visual = optionVisual(option);
    return visual ? <div className="pt-[3px] shrink-0">{visual}</div> : null;
  };

  const btnText = React.useMemo(() => {
    const selected = !!value?.name;
    return (
      <div className="flex items-center gap-2 min-w-0">
        <span className="shrink-0 flex">
          {icon?.({ width: 18, height: 18, stroke: "#B0B0B0" })}
        </span>
        <Typography
          title={selected ? value?.name : undefined}
          className={`!text-[13px] truncate ${
            selected ? "text-text-1 font-bold" : "text-text-2"
          }`}
        >
          {selected ? value?.name : title}
        </Typography>
      </div>
    );
  }, [value, title, icon]);

  return (
    <ClickAwayListener onClickAway={() => setShowOptions(false)}>
      <div className="relative bg-surface rounded-[10px]">
        <button
          onClick={() => setShowOptions(!showOptions)}
          className={classNames(
            // Bar spec (matches the More filters button): 40px, r10, 13px label.
            // Border scale: --border in light, --muted in dark (see CustomSelect).
            `rounded-[10px] border border-border-token dark:border-muted min-w-[140px] h-10 text-[13px] cursor-pointer`,
            {
              "bg-surface-2 font-bold": value,
            }
          )}
          style={{ width: "100%" }}
          type="button"
        >
          <div className="flex items-center justify-between gap-[6px] px-[13px] min-w-0">
            {btnText}
            <span className="shrink-0 flex">
              {showOptions ? <IconArrowUp /> : <IconArrowDown />}
            </span>
          </div>
        </button>
        {showOptions && (
          <div
            className={`absolute left-0 xl:right-0 xl:left-auto ${isMobile ? "right-0" : ""}`}
            style={{ top: 48, left: 0 }}
          >
            <Autocomplete
              open
              disablePortal={isMobile}
              value={value}
              disableCloseOnSelect
              {...(onSearchChange ? { filterOptions: (x: ISingleOption[]) => x } : {})}
              onInputChange={(_: any, inputValue: string, reason: string) => {
                if (reason === "input" && onSearchChange) {
                  onSearchChange(inputValue);
                }
              }}
              sx={{
                minWidth: isMobile ? "100%" : 300,
                maxHeight: 300,
                "& .filter-input .MuiInputBase-input": { fontSize: 13 },
                "& .filter-input .MuiInputBase-input::placeholder": { fontSize: 13 },
                "& .filter-input .MuiAutocomplete-popupIndicator": {
                  display: "none",
                },
                "& .filter-input .MuiOutlinedInput-root": {
                  paddingRight: "12px !important",
                },
                "&.MuiAutocomplete-hasClearIcon .filter-input .MuiOutlinedInput-root":
                  { paddingRight: "39px !important" },
              }}
              options={optionsObjArray || []}
              getOptionLabel={(option) => option.name}
              onChange={onFilterValueChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Search ${title}`}
                  className="bg-surface-2 rounded-xl overflow-hidden filter-input z-10"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconSearch />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <ListItem
                  {...props}
                  className={classNames(
                    "flex p-3 !items-start cursor-pointer hover:bg-surface-2",
                    {
                      "bg-tint pointer-events-none":
                        value?.id === option.id,
                    }
                  )}
                  key={option.id}
                  sx={{ gap: 1 }}
                >
                  {renderOptionVisual(option)}
                  <ListItemText
                    className="m-0"
                    primary={option.name}
                    primaryTypographyProps={{ fontSize: 13 }}
                  />
                  {value?.id === option.id && (
                    <div className="pt-2">
                      <IconCheck />
                    </div>
                  )}
                </ListItem>
              )}
              {...(isMobile
                ? { slotProps: { popper: { sx: { width: "100% !important" } } } }
                : {})}
              PaperComponent={({ children }) => (
                <Paper {...(isMobile ? { sx: { width: "100%" } } : {})}>
                  {onCreateNew && (
                    <Button
                      className="flex gap-2 !items-center !justify-start w-full text-accent-text !capitalize !px-4 !mt-0 !py-2"
                      onClick={onCreateNew}
                    >
                      <IconPlus stroke="#AB854A" />
                      <Typography variant="body1" sx={{ fontSize: 13 }}>
                        Create new {title}
                      </Typography>
                    </Button>
                  )}
                  {children}
                </Paper>
              )}
            />
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}
